import React, { useEffect, useState } from 'react';

import FourColumnGrid from 'molecules/FourColumnGrid';
import LeaderShipCard from 'molecules/LeaderShipCard';
import RichText from 'molecules/RichText';
import ThreeColumnGrid from 'molecules/ThreeColumnGrid';

import styles from 'components/LeaderShipDeck/leadershipdeck.module.scss';

import { isNonEmptyArray } from 'utils/functions';

import type { LeaderShipDeckProps } from 'components/LeaderShipDeck/config';
import type { FC } from 'react';

const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
};

const LeaderShipDeck: FC<LeaderShipDeckProps> = ({
  heading,
  type,
  leadershipCards,
  enableRandomization,
  cardToStartRandomization = 3,
  footerNotes,
}) => {
  const [cards, setCards] = useState<typeof leadershipCards | null>(leadershipCards);
  const [isRandomized, setIsRandomized] = useState(false);

  useEffect(() => {
    if (!isNonEmptyArray(leadershipCards)) {
      setCards(null);

      return;
    }

    const validStartIndex =
      cardToStartRandomization > leadershipCards.length ? leadershipCards.length : cardToStartRandomization - 1;

    if (enableRandomization && validStartIndex < leadershipCards.length) {
      const unshuffled = leadershipCards.slice(0, validStartIndex);
      const shuffled = shuffleArray(leadershipCards.slice(validStartIndex));
      setCards([...unshuffled, ...shuffled]);
      setIsRandomized(true);
    } else {
      setCards(leadershipCards);
    }
  }, [enableRandomization, leadershipCards, cardToStartRandomization]);

  if (enableRandomization && !isRandomized) {
    return null;
  }

  const renderCards = () => cards?.map((card, index) => <LeaderShipCard {...card} ratio={type} key={index} />);

  return (
    <>
      {heading && (
        <div className={styles.headingContainer}>
          <RichText content={heading} className={styles.heading} />
        </div>
      )}
      <div className={styles.container}>
        {type === 'Square' ? (
          <FourColumnGrid>{renderCards()}</FourColumnGrid>
        ) : (
          <ThreeColumnGrid classes={{ grid: styles.customGrid }}>{renderCards()}</ThreeColumnGrid>
        )}
        {footerNotes && <RichText content={footerNotes} className={styles.footerNotes} />}
      </div>
    </>
  );
};

export default LeaderShipDeck;
